import React, { useState } from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import { useSelector } from "react-redux";

import Sidebar from "../../components/sidebar/Sidebar";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import CrmSyncBar from "../../components/commercial-dashboard/CrmSyncBar";
import SummaryTab from "../../components/commercial-dashboard/SummaryTab";
import CommercialPipelineTab from "../../components/commercial-dashboard/CommercialPipelineTab";
import ProposalsPipelineTab from "../../components/commercial-dashboard/ProposalsPipelineTab";
import DashboardFilters, {
  FiltersSummaryLine,
  FilterState,
  INITIAL_FILTERS,
  toParams,
} from "../../components/commercial-dashboard/DashboardFilters";
import DashboardSheets, {
  SheetTarget,
} from "../../components/commercial-dashboard/DashboardSheets";
import SettingsDialog from "../../components/commercial-dashboard/SettingsDialog";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { useUserRole } from "../../hooks/useUserRole";
import { useGetDashboardFilterOptionsQuery } from "../../store/apis/commercialDashboardApi";

type Section = "summary" | "commercial" | "proposals";

/**
 * Dashboard comercial.
 *
 * Tres apartados, como en la especificación: Resumen ejecutivo, Pipeline comercial (las
 * empresas de New Business, copiadas de Twenty) y Pipeline de propuestas (las nuestras).
 * ADMIN, PRICING y los comerciales (que de momento entran como ADMIN) ven los tres;
 * ACCOUNT solo el de propuestas.
 */
const CommercialDashboard: React.FC = () => {
  const theme = useTheme();
  const collapsed = useSelector(selectSidebarCollapsed);
  const { role } = useUserRole();

  const canSeeAll = role === "ADMIN" || role === "PRICING";
  const canSync = canSeeAll || role === "STRATEGY";
  const canEditSettings = role === "ADMIN";

  const [section, setSection] = useState<Section>(
    canSeeAll ? "summary" : "proposals"
  );
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);
  const [sheet, setSheet] = useState<SheetTarget | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { data: options } = useGetDashboardFilterOptionsQuery();

  const activeSection: Section = canSeeAll ? section : "proposals";
  const params = toParams(filters);
  const isCommercial = activeSection === "commercial";

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar />

      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 2, md: 4 },
          py: { xs: 2, md: 3 },
          transition: "margin-left 0.3s ease",
          ...(collapsed
            ? { ml: `calc(${theme.spacing(7)} + 12px)` }
            : { ml: `calc(${theme.spacing(32)} + 12px)` }),
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <PageBreadcrumbs />

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${theme.palette.grey[200]}`,
            p: { xs: 2, md: 3 },
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            gap={1}
          >
            <Box display="flex" flexDirection="column" gap={0.5}>
              <Typography variant="h5">Dashboard comercial</Typography>
              <Typography variant="body2" color="text.secondary">
                Captación de clientes nuevos, todas las propuestas y qué
                necesita una decisión esta semana.
              </Typography>
            </Box>
            {canEditSettings && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<TuneIcon />}
                onClick={() => setSettingsOpen(true)}
              >
                Parámetros
              </Button>
            )}
          </Stack>
          <DashboardFilters
            value={filters}
            onChange={setFilters}
            owners={options?.data?.owners ?? []}
            showBusinessLine={!isCommercial}
            showHouse={!isCommercial}
          />
          <FiltersSummaryLine state={filters} />
        </Paper>

        <CrmSyncBar canSync={canSync} />

        {canSeeAll && (
          <Tabs
            value={activeSection}
            onChange={(_, value: Section) => setSection(value)}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{ borderBottom: `1px solid ${theme.palette.grey[200]}` }}
          >
            <Tab value="summary" label="Resumen ejecutivo" />
            <Tab value="commercial" label="Pipeline comercial" />
            <Tab value="proposals" label="Pipeline de propuestas" />
          </Tabs>
        )}

        {activeSection === "summary" && (
          <SummaryTab params={params} onOpen={setSheet} />
        )}
        {activeSection === "commercial" && (
          <CommercialPipelineTab params={params} onOpen={setSheet} />
        )}
        {activeSection === "proposals" && (
          <ProposalsPipelineTab params={params} onOpen={setSheet} />
        )}
      </Box>

      <DashboardSheets
        target={sheet}
        onClose={() => setSheet(null)}
        onOpen={setSheet}
      />
      {canEditSettings && (
        <SettingsDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </Box>
  );
};

export default CommercialDashboard;
